/**
 * Reliable edge function caller that works on both web and native (Capacitor).
 *
 * On native, Capacitor's HTTP plugin can strip the Authorization header from
 * requests made via supabase.functions.invoke(). This helper uses direct
 * fetch() with explicit headers to guarantee the auth token reaches the
 * backend every time.
 */

import { supabase } from "@/integrations/supabase/client";
import { captureEvent, captureMetric, getCorrelationId } from "@/lib/observability";

/** Default hard timeout so a hung request can never block a caller forever. */
const DEFAULT_TIMEOUT_MS = 45_000;

export async function invokeEdgeFunction<T = any>(
  functionName: string,
  body: Record<string, unknown>,
  options?: { timeoutMs?: number },
): Promise<{ data: T | null; error: { message: string } | null }> {
  const startedAt = performance.now();
  const timeoutMs = options?.timeoutMs ?? DEFAULT_TIMEOUT_MS;
  const controller = new AbortController();
  let timedOut = false;
  const timer = setTimeout(() => {
    timedOut = true;
    controller.abort();
  }, timeoutMs);
  try {
    const session = await supabase.auth.getSession();
    const accessToken = session.data.session?.access_token;
    if (!accessToken) {
      return { data: null, error: { message: "No auth session — please sign in again" } };
    }

    const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
    const anonKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

    const response = await fetch(`${supabaseUrl}/functions/v1/${functionName}`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${accessToken}`,
        "apikey": anonKey,
        "x-correlation-id": getCorrelationId(),
      },
      body: JSON.stringify(body),
      signal: controller.signal,
    });

    const data = await response.json().catch(() => null);

    if (!response.ok) {
      const msg = data?.error || `HTTP ${response.status}`;
      captureEvent({
        kind: "error",
        component: `api.${functionName}`,
        message: msg,
        duration_ms: performance.now() - startedAt,
        metadata: { status: response.status },
      });
      return { data: null, error: { message: msg } };
    }


    captureMetric(`api.${functionName}`, "edge_function_ms", performance.now() - startedAt);
    return { data, error: null };
  } catch (err: any) {
    const msg = timedOut
      ? `Request timed out after ${Math.round(timeoutMs / 1000)}s`
      : err?.message || "Network request failed";
    captureEvent({
      kind: "error",
      component: `api.${functionName}`,
      message: msg,
      duration_ms: performance.now() - startedAt,
      metadata: { network: true, timeout: timedOut },
    });
    return { data: null, error: { message: msg } };
  } finally {
    clearTimeout(timer);
  }
}

